import { FaSitemap as SetoresIcon } from 'react-icons/fa'

import { PermissaoTipo, Setor } from '@core/models/entities'
import { useAuth, useFetch } from '@presentation/hooks'
import { TablePage } from '@presentation/components/templates'
import { PageProps } from '@presentation/types/components/templates'
import {
  Filter,
  FilterProps,
  TableColumn,
  TableProps
} from '@presentation/types/components/organisms'

import {
  AddSetorModal,
  DeleteSetorModal,
  EditSetorModal,
  ReactivateSetorModal
} from './components'

const SetoresPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const { data, isFetching, totalItems, fetchData } = useFetch({
    path: '/setores',
    options: { sortBy: 'nome' },
    queries: {
      status: 1
    }
  })

  const pageConfigs: PageProps = {
    icon: <SetoresIcon />,
    title: 'Setores',
    description: 'Página de gerenciamento de setores do campus.',
    actions: hasPermission(PermissaoTipo.Escrita)
      ? [<AddSetorModal onAdd={fetchData} />]
      : []
  }

  const filters: Filter[] = [
    {
      type: {
        value: 'nome',
        label: 'Nome'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'email',
        label: 'Email'
      },
      description: {
        type: 'input',
        configs: {
          type: 'text'
        }
      }
    },
    {
      type: {
        value: 'lotacao_id',
        label: 'Lotação'
      },
      description: {
        type: 'select-api',
        configs: {
          path: '/lotacoes',
          isMulti: true
        }
      }
    },
    {
      type: {
        value: 'local_id',
        label: 'Local'
      },
      description: {
        type: 'select-api',
        configs: {
          path: '/locais',
          isMulti: true
        }
      }
    }
  ]

  const filterConfigs: FilterProps = {
    filters,
    fetchData,
    defaultQueries: {
      status: 1
    }
  }

  const columns: Array<TableColumn<Setor>> = [
    {
      name: 'Nome',
      selector: setor => setor.nome,
      sortable: true,
      center: true,
      grow: 2
    },
    {
      name: 'Email',
      selector: setor => setor.email,
      sortable: true,
      center: true
    },
    {
      name: 'Lotação',
      selector: setor => setor.lotacao?.nome ?? '',
      sortable: true,
      center: true
    },
    {
      name: 'Local',
      selector: setor => setor.local?.nome ?? '',
      sortable: true,
      center: true
    },
    {
      name: 'Ações',
      cell: setor => (
        <>
          {setor.status ? (
            <>
              {hasPermission(PermissaoTipo.Escrita) && (
                <EditSetorModal id={setor.id} onEdit={fetchData} />
              )}
              {hasPermission(PermissaoTipo.Remocao) && (
                <DeleteSetorModal id={setor.id} onDelete={fetchData} />
              )}
            </>
          ) : (
            <>
              {hasPermission(PermissaoTipo.Escrita) && (
                <ReactivateSetorModal id={setor.id} onReactivate={fetchData} />
              )}
            </>
          )}
        </>
      ),
      center: true
    }
  ]

  const tableConfigs: TableProps = {
    columns,
    data,
    apiConfigs: {
      totalItems,
      fetchData,
      isFetching
    }
  }

  return (
    <TablePage
      pageConfigs={pageConfigs}
      filterConfigs={filterConfigs}
      tableConfigs={tableConfigs}
    />
  )
}

export default SetoresPage
